/**
 * ROUND8: how a dog gets about. Straight at where it is going when nothing solid and no water lies in between - a
 * trail's prints are a stride apart, and a dog at heel or at bay is a few metres from the man it is keeping to - and
 * by the soldiers' nav grid (`travel`) round anything that does.
 */

import type { Ctx } from '../../ctx';
import { atan2, cos, HALF_PI, sin } from '../../math';
import { resolveMovement, segmentHit } from '../../statics';
import { DOGS } from '../../tuningDogs';
import { DT, type Enemy } from '../../types';
import { cellFree, travel, turnToward } from '../movement';
import { waterBetween } from './dogNose';

/** Further than this, metres, it always takes the grid: a straight line that long is too likely to meet a wall. */
const DIRECT_REACH = 8;
/** Radians a second it turns: quicker on its feet than a man (8). */
export const DOG_TURN_RATE = 10;

/** Nothing that stops a body stands on the line, and no water lies across it. */
function clearWay(ctx: Ctx, ax: number, az: number, bx: number, bz: number): boolean {
  for (const c of ctx.statics.colliders) if (c.blocksMove && segmentHit(c, ax, az, bx, bz)) return false;
  return !waterBetween(ctx.statics.def, ax, az, bx, bz);
}

/** Toward (x, z) at `speed` metres a second. True once within `arrive` of it (or when there is no way there). */
export function go(ctx: Ctx, e: Enemy, x: number, z: number, speed: number, arrive: number): boolean {
  const dx = x - e.x;
  const dz = z - e.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d <= arrive) {
    e.moving = false;
    return true;
  }
  if (d > DIRECT_REACH || !clearWay(ctx, e.x, e.z, x, z)) {
    // `travel` leaves `moving` alone on a tick it spends waiting its turn for a path.
    e.moving = false;
    return travel(ctx, e, x, z, speed, arrive);
  }
  // Never nearer than `arrive` by its own feet (a centimetre inside it, so the next tick counts as there): a dog at
  // bay stops at `DOGS.bayDistance` and does not creep on.
  const step = Math.min(d - arrive + 0.01, speed * DT);
  const moved = resolveMovement(ctx.statics, e.x + (dx / d) * step, e.z + (dz / d) * step, DOGS.radius);
  e.travelled += Math.sqrt((moved.x - e.x) * (moved.x - e.x) + (moved.z - e.z) * (moved.z - e.z));
  e.x = moved.x;
  e.z = moved.z;
  e.moving = true;
  // A path asked for before is for somewhere else by now.
  e.path = [];
  turnToward(e, atan2(dx, dz), DOG_TURN_RATE);
  return false;
}

/**
 * Where a dog walks at heel: a pace off its handler's left shoulder, where the generator puts it down
 * (gen/dogTeams.ts). If that spot is inside something solid, his own feet, and `arrive` says how near to come.
 */
export function heelSpot(ctx: Ctx, h: Enemy): { x: number; z: number; arrive: number } {
  const side = h.facing + HALF_PI;
  const x = h.x + sin(side) * DOGS.heelDistance;
  const z = h.z + cos(side) * DOGS.heelDistance;
  if (cellFree(ctx.statics, x, z)) return { x, z, arrive: 0.4 };
  return { x: h.x, z: h.z, arrive: DOGS.heelDistance };
}

/** Stand still, turning to look at a point. */
export function standFacing(e: Enemy, x: number, z: number): void {
  e.moving = false;
  if ((x - e.x) * (x - e.x) + (z - e.z) * (z - e.z) > 1e-6) turnToward(e, atan2(x - e.x, z - e.z), DOG_TURN_RATE);
}
